import { ColumnDef } from '@tanstack/react-table'
import { SortingHeaderButton } from '@/components/ui/DataTable/SortingHeaderButton'
import { RestSchemas } from '@/contexts/QueryProvider'
import { Badge, BadgeProps } from '@/components/shadcn/badge'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { format } from 'date-fns'
import { LogsContent, LogsDialog } from './LogsDialog'

type MessagesDagsterShortRun = RestSchemas['Messages.Dagster.ShortRun']

const runStatusMap: Record<NonNullable<MessagesDagsterShortRun['runStatus']>, BadgeProps['variant']> = {
  SUCCESS: 'success',
  FAILURE: 'destructive',
}

export const dagsterApplicationRunsColumns: ColumnDef<MessagesDagsterShortRun>[] = [
  {
    id: 'runId',
    accessorFn: (row) => row.id,
    header: 'Run ID',
    cell: ({ cell }) => cell.getValue() as string,
    enableGlobalFilter: true,
  },
  {
    id: 'tags',
    accessorFn: (row) => row.tags?.map((tag) => tag.value).join(', '),
    header: 'Tags',
    cell: ({ row }) => (
      <div className="flex flex-wrap gap-1">
        {row.original.tags?.map(({ key, value }) => {
          return (
            <Badge variant="secondary" key={key}>
              <span className="font-light">{key}: </span>
              {key === 'credentials' ? (
                <TooltipButton tooltip={value} contentClassName="max-w-xl">
                  <span className="ml-1 max-w-20 truncate">{value}</span>
                </TooltipButton>
              ) : (
                <span className="ml-1">{value}</span>
              )}
            </Badge>
          )
        })}
      </div>
    ),
  },
  {
    id: 'jobName',
    accessorFn: (row) => row.jobName,
    header: ({ column }) => <SortingHeaderButton column={column}>Job name</SortingHeaderButton>,
    cell: ({ cell }) => cell.getValue() as string,
    sortingFn: (rowA, rowB) => (rowA.original.jobName ?? '').localeCompare(rowB.original.jobName ?? ''),
    enableGlobalFilter: true,
  },
  {
    id: 'creationTime',
    accessorFn: (row) => row.creationTime,
    header: ({ column }) => <SortingHeaderButton column={column}>Creation time</SortingHeaderButton>,
    cell: ({ cell }) => format((cell.getValue() as number) * 1000, 'PPp'),
    sortingFn: (rowA, rowB) => (rowA.original.creationTime ?? 0) - (rowB.original.creationTime ?? 0),
    enableGlobalFilter: false,
  },
  {
    id: 'duration',
    accessorFn: (row) => (row.startTime && row.endTime ? row.endTime - row.startTime : 0),
    header: ({ column }) => <SortingHeaderButton column={column}>Duration</SortingHeaderButton>,
    cell: ({ cell }) => <span>{(cell.getValue() as number).toFixed(2)}s</span>,
    enableGlobalFilter: false,
  },
  {
    id: 'runStatus',
    accessorFn: (row) => row.runStatus,
    header: 'Run status',
    sortingFn: (rowA, rowB) => (rowA.original.runStatus ?? '').localeCompare(rowB.original.runStatus ?? ''),
    cell: ({ cell }) => (
      <Badge variant={runStatusMap[cell.getValue() as NonNullable<MessagesDagsterShortRun['runStatus']>] ?? 'outline'}>
        {cell.getValue() as string}
      </Badge>
    ),
    enableGlobalFilter: true,
  },
]
